import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Copy, Edit, Trash2, AlertTriangle, Eye, EyeOff, Loader2, MoreVertical, Code } from "lucide-react";
import type { Deck } from "@/lib/api/deck-api";
import { copyDeckCodeToClipboard } from "@/lib/utils/deck-library";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Deck as DeckClass } from "@/lib/classes/deck";
import type { Division } from "@/lib/types/deck-builder";
import type { UnitMap } from "@/lib/types/deck-builder";
import { DeckPreview } from "@/components/DeckDrafter/DeckPreview";
import { getDivisionImagePath, getCountryFlagPath } from "@/lib/utils/division-images";
import { toast } from "sonner";

interface DeckCardProps {
  deck: Deck;
  isOwner: boolean;
  divisions: Division[];
  unitMap: UnitMap;
  onEdit?: (deck: Deck) => void;
  onEditWithCode?: (deck: Deck) => void;
  onDelete?: (deck: Deck) => void;
  onCopy?: () => void;
}

export function DeckCard({ deck, isOwner, divisions, unitMap, onEdit, onEditWithCode, onDelete, onCopy }: DeckCardProps) {
  const navigate = useNavigate();
  const [copying, setCopying] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDeck, setPreviewDeck] = useState<DeckClass | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Load deck preview when preview is opened
  useEffect(() => {
    if (previewOpen && !previewDeck && !loadingPreview && !previewError) {
      setLoadingPreview(true);
      setPreviewError(null);
      
      try {
        const loadedDeck = DeckClass.fromDeckCode(deck.deck_code, {
          unitMap,
          divisions,
        });
        setPreviewDeck(loadedDeck);
      } catch (error) {
        setPreviewError(
          error instanceof Error
            ? error.message
            : "Failed to load deck preview"
        );
        console.error("Failed to load deck preview:", error);
      } finally {
        setLoadingPreview(false);
      }
    }
  }, [previewOpen, deck.deck_code, unitMap, divisions, previewDeck, loadingPreview, previewError]);

  const handleCopy = async () => {
    try {
      setCopying(true);
      await copyDeckCodeToClipboard(deck.deck_code);
      onCopy?.();
    } catch (error) {
      console.error("Failed to copy deck code:", error);
      toast.error("Failed to copy deck code to clipboard");
    } finally {
      setCopying(false);
    }
  };

  const handlePreviewToggle = () => {
    setPreviewOpen(!previewOpen);
    if (previewOpen) {
      // When closing, optionally clear the preview to save memory
      // setPreviewDeck(null);
      // setPreviewError(null);
    }
  };

  // Find the division from the divisions array
  const division = divisions.find(d => d.descriptor === deck.division_descriptor);

  // Parse tags from comma-separated string
  const deckTags = deck.tags
    ? deck.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
    : [];

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full">
          {/* Top Row: Division Icons, Name, and Action Buttons */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Division Icon and Nation Flag */}
            {division && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <img
                  src={getDivisionImagePath(division.descriptor)}
                  alt={division.name}
                  className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <img
                  src={getCountryFlagPath(division.country)}
                  alt={division.country}
                  className="h-6 w-6 sm:h-7 sm:w-7 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
            
            {/* Deck Name and Metadata */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <CardTitle 
                  className="text-sm sm:text-base font-semibold break-words cursor-pointer hover:underline"
                  onClick={() => navigate(`/deck/${deck.id}`)}
                >
                  {deck.name}
                </CardTitle>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {deck.isOutdated && (
                    <Badge variant="destructive" className="flex items-center gap-1 text-xs">
                      <AlertTriangle className="h-3 w-3" />
                      Outdated
                    </Badge>
                  )}
                  {!deck.is_public && (
                    <Badge variant="secondary" className="text-xs">Private</Badge>
                  )}
                </div>
              </div>
              <div className="text-xs text-muted-foreground break-words">
                <span className="break-all">{deck.author_name || deck.discord_username || 'Unknown'}</span>
                <span className="mx-1">•</span>
                <span className="whitespace-nowrap">
                  {new Date(deck.created_at).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </span>
                {deck.updated_at !== deck.created_at && (
                  <>
                    <span className="mx-1">•</span>
                    <span className="whitespace-nowrap">
                      Updated: {new Date(deck.updated_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      })}
                    </span>
                  </>
                )}
              </div>
              {deckTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {deckTags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs py-0.5 px-1.5">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 justify-end sm:justify-start">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              disabled={copying}
              title="Copy Code"
              className="h-8 w-8 sm:h-9 sm:w-9 p-0"
            >
              <Copy className="h-4 w-4" />
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviewToggle}
              title={previewOpen ? "Hide Preview" : "Show Preview"}
              className="h-8 w-8 sm:h-9 sm:w-9 p-0"
            >
              {previewOpen ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            
            {isOwner && (
              <>
                {onEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(deck)}
                    title="Edit Deck in Builder"
                    className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
                {(onEditWithCode || onDelete) ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        title="More options"
                        className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-1" align="end">
                      <div className="flex flex-col gap-1">
                        {onEditWithCode && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="justify-start"
                            onClick={() => onEditWithCode(deck)}
                          >
                            <Code className="h-4 w-4 mr-2" />
                            Edit with Code
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="justify-start text-destructive hover:text-destructive"
                            onClick={() => onDelete(deck)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : null}
              </>
            )}
          </div>
        </div>
        
        {/* Inline Preview Section */}
        {previewOpen && (
          <div className="mt-4 pt-4 border-t">
            {loadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : previewError ? (
              <div className="text-center text-destructive py-8">
                {previewError}
              </div>
            ) : previewDeck ? (
              <div className="overflow-x-auto">
                <DeckPreview deck={previewDeck} />
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


